import { Target, Lightbulb, Video, Palette, Globe, ArrowRight } from "lucide-react";
import type { MouseEvent } from "react";
import { Reveal } from "@/components/Reveal";

export const EcosystemSection = () => {
  const handleCardMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateY = ((x - centerX) / centerX) * 12;
    const rotateX = -((y - centerY) / centerY) * 12;
    const translateX = ((x - centerX) / centerX) * 18;
    const translateY = ((y - centerY) / centerY) * 18;

    el.style.setProperty("--mx", `${x}px`);
    el.style.setProperty("--my", `${y}px`);
    el.style.setProperty("--rx", `${rotateX.toFixed(2)}deg`);
    el.style.setProperty("--ry", `${rotateY.toFixed(2)}deg`);
    el.style.setProperty("--tx", `${translateX.toFixed(2)}px`);
    el.style.setProperty("--ty", `${translateY.toFixed(2)}px`);
  };

  const handleCardMouseLeave = (e: MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.style.setProperty("--mx", "50%");
    el.style.setProperty("--my", "50%");
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--tx", "0px");
    el.style.setProperty("--ty", "0px");
  };

  const pillars = [
    { icon: Target, label: "Pauta", desc: "Multi-plataforma" },
    { icon: Lightbulb, label: "Estrategia", desc: "Basada en datos" },
    { icon: Video, label: "Contenido", desc: "Profesional" },
    { icon: Palette, label: "Diseño", desc: "De alto impacto" },
    { icon: Globe, label: "Web", desc: "Optimizada" },
  ];

  return (
    <section id="ecosystem" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center mb-12 sm:mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/25 border border-primary/45 text-primary text-xs sm:text-sm font-medium mb-4">
            Nuestro diferenciador
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4 sm:mb-6">
            Tu ecosistema completo de marketing
          </h2>
          <p className="text-base sm:text-lg text-primary max-w-2xl mx-auto">
            No contratás servicios sueltos. Contratás un sistema donde todo está conectado para generar resultados.
          </p>
        </Reveal>

        {/* Ecosystem visual */}
        <Reveal delay={1}>
          <div className="relative">
            {/* Desktop: horizontal flow */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-2 lg:gap-4">
              {pillars.map((pillar, i) => {
                const Icon = pillar.icon;
                return (
                  <div key={i} className="flex items-center gap-2 sm:gap-1 lg:gap-3">
                    <div
                      className="group relative flex flex-col items-center text-center w-28 sm:w-24 lg:w-32 rounded-2xl px-2 py-3 transition-transform duration-75 will-change-transform [transform:translate3d(var(--tx,0px),var(--ty,0px),0)_perspective(850px)_rotateX(var(--rx,0deg))_rotateY(var(--ry,0deg))_scale(1.03)]"
                      onMouseMove={handleCardMouseMove}
                      onMouseLeave={handleCardMouseLeave}
                    >
                      <div
                        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                        style={{
                          background:
                            "radial-gradient(140px circle at var(--mx,50%) var(--my,50%), hsl(var(--primary) / 0.38), transparent 68%)",
                        }}
                      />
                      <div className="relative p-3 sm:p-4 bg-primary/30 rounded-2xl mb-2 sm:mb-3 group-hover:bg-primary/45 transition-colors">
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
                      </div>
                      <span className="relative text-sm sm:text-base font-bold text-accent">{pillar.label}</span>
                      <span className="relative text-xs text-primary">{pillar.desc}</span>
                    </div>
                    {i < pillars.length - 1 && (
                      <ArrowRight className="h-4 w-4 text-primary/40 hidden sm:block flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Result */}
            <div className="mt-8 sm:mt-10 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 sm:px-8 sm:py-4 rounded-full bg-primary/30 border border-primary/45">
                <span className="text-sm sm:text-base lg:text-lg font-bold text-primary">
                  = Resultados medibles y escalables
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={2} className="text-center mt-10 sm:mt-12">
          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">
            Todo conectado. Todo medible.{" "}
            <span className="bg-primary/55 text-primary-foreground px-3 py-1 rounded-2xl inline-block mt-2">Todo en un solo equipo.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
